import { Eyebrow } from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { SITE } from "@/constants/site";

const values = [
    {
        title: "Clarity",
        desc: "Numbers explained in plain English. If you don't understand it, we haven't finished our job.",
    },
    {
        title: "Warmth",
        desc: "Finance is personal when it's your business. You're a person with a dream, not a file number.",
    },
    {
        title: "Precision",
        desc: "Detail-obsessed, deadline-true, and reconciled to the penny.",
    },
    {
        title: "Proactivity",
        desc: "We flag issues in June instead of apologizing for them in April.",
    },
];

const comparison = [
    ["You're handed to a junior team", "Your CPA does the work, personally"],
    ["Jargon-heavy annual meetings", "Plain-English numbers, any time you ask"],
    ["Surprise invoices by the hour", "Flat fees quoted in writing, up front"],
    ["Shoeboxes and paper trails", "Modern, secure, paperless tools"],
];

export default function About() {
    return (
        <div data-testid="page-about">
            <section className="border-b border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>About us</Eyebrow>
                    <h1 className="mt-6 max-w-2xl font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl">
                        A new firm,{" "}
                        <span className="italic text-gold-deep">
                            built on purpose.
                        </span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-clay sm:text-lg">
                        Phillip CPA is a brand-new practice — and we think
                        that's the best thing about us.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <Eyebrow>Our story</Eyebrow>
                        <h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                            Small businesses deserve big-firm quality.
                        </h2>
                        <div className="mt-6 space-y-5 text-base leading-relaxed text-clay">
                            <p>
                                Too many great businesses get accounting
                                that's either impersonal or an afterthought —
                                giant firms where they're a number, or DIY
                                software where they're alone. Phillip CPA
                                exists in the space between: professional-grade
                                work, delivered by a person who knows your
                                name.
                            </p>
                            <p>
                                We're at the beginning of our own story, which
                                means every client we take on matters deeply —
                                not just this quarter, but for the long haul.
                                When you call, a CPA answers. When we quote a
                                fee, it's flat and in writing. When your books
                                close each month, you actually understand them.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div
                            className="rounded-2xl border border-ink/10 bg-bone p-8"
                            data-testid="about-credentials-panel"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                                Your CPA
                            </p>
                            <p className="mt-4 font-serif text-2xl font-medium">
                                Details coming soon.
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-clay">
                                Name, license, and credentials will be posted
                                here shortly.
                            </p>
                            <a
                                href={`mailto:${SITE.email}`}
                                data-testid="about-credentials-email"
                                className="mt-6 inline-block text-sm font-semibold text-gold-deep hover:underline"
                            >
                                Ask in the meantime: {SITE.email}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-y border-ink/10 bg-bone">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                    <Eyebrow>What we stand for</Eyebrow>
                    <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {values.map((v, i) => (
                            <div
                                key={v.title}
                                className="rounded-2xl border border-ink/10 bg-cream p-8 transition-colors hover:border-gold"
                                data-testid="about-value-card"
                            >
                                <p className="font-mono text-[11px] tracking-[0.25em] text-gold-deep">
                                    0{i + 1}
                                </p>
                                <h3 className="mt-4 font-serif text-2xl font-medium">
                                    {v.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-clay">
                                    {v.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                <Eyebrow>Why a new firm</Eyebrow>
                <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                    The old way vs. the Phillip way.
                </h2>
                <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div
                        className="rounded-2xl border border-ink/10 p-8"
                        data-testid="comparison-old"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                            Traditional firms
                        </p>
                        <ul className="mt-6 space-y-4">
                            {comparison.map(([old]) => (
                                <li
                                    key={old}
                                    className="text-sm text-clay line-through decoration-clay/40 sm:text-base"
                                >
                                    {old}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className="rounded-2xl border border-gold/50 bg-bone p-8"
                        data-testid="comparison-phillip"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-deep">
                            The Phillip way
                        </p>
                        <ul className="mt-6 space-y-4">
                            {comparison.map(([, nw]) => (
                                <li
                                    key={nw}
                                    className="flex items-start gap-3 text-sm sm:text-base"
                                >
                                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                                    {nw}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <CTABanner />
        </div>
    );
}
